const FAVORITES_KEY = 'flourish-favorites-v1';

class UxFavorites extends HTMLElement {
  connectedCallback() {
    this.items = this.read();
    this.dialog = this.querySelector('dialog');
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    document.addEventListener('click', this.onClick, { ...options, capture: true });
    window.addEventListener('storage', this.onStorage, options);
    this.observer = new MutationObserver(() => this.syncButtons());
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.syncButtons();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  read() {
    try {
      const stored = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
      if (!Array.isArray(stored)) return [];
      return stored.filter(item => item && /^\d+$/.test(item.id) && typeof item.title === 'string' && this.safeUrl(item.url) && this.safeUrl(item.image, true)).slice(0, 200);
    } catch {
      return [];
    }
  }

  safeUrl(value, image = false) {
    try {
      if (typeof value !== 'string') return false;
      const url = new URL(value, location.origin);
      return url.protocol === 'https:' && (url.origin === location.origin || (image && url.hostname === 'cdn.shopify.com'));
    } catch {
      return false;
    }
  }

  onStorage = (event) => {
    if (event.key !== FAVORITES_KEY) return;
    this.items = this.read();
    this.syncButtons();
    if (this.dialog.open) this.renderList();
  };

  onClick = (event) => {
    const button = event.target.closest('button');
    if (!button) return;
    if (button.matches('[data-ux-favorite]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const id = button.dataset.productId;
      const exists = this.items.some(item => item.id === id);
      const next = exists ? this.items.filter(item => item.id !== id) : [...this.items, { id, title: button.dataset.title, url: button.dataset.url, image: button.dataset.image }];
      this.save(next, exists ? this.dataset.removed : this.dataset.saved);
    } else if (button.matches('[data-ux-open]')) {
      this.renderList();
      this.dialog.showModal();
    } else if (button.matches('[data-ux-close]')) {
      this.dialog.close();
    } else if (button.matches('[data-ux-remove]')) {
      this.save(this.items.filter(item => item.id !== button.dataset.uxRemove), this.dataset.removed);
      this.renderList();
      (this.querySelector('[data-ux-remove]') || this.querySelector('[data-ux-close]')).focus();
    }
  };

  save(next, message) {
    try {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
      this.items = next;
      this.syncButtons();
      this.querySelector('[data-ux-status]').textContent = message;
    } catch {
      this.querySelector('[data-ux-status]').textContent = this.dataset.unavailable;
    }
  }

  syncButtons() {
    document.querySelectorAll('[data-ux-favorite]').forEach(button => {
      const active = this.items.some(item => item.id === button.dataset.productId);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', `${active ? this.dataset.remove : this.dataset.save}: ${button.dataset.title}`);
    });
    const count = this.querySelector('[data-ux-count]');
    if (count.textContent !== String(this.items.length)) count.textContent = String(this.items.length);
  }

  renderList() {
    const list = this.querySelector('[data-ux-favorites-list]');
    const fragment = document.createDocumentFragment();
    if (!this.items.length) {
      const empty = document.createElement('p');
      empty.textContent = this.dataset.empty;
      fragment.append(empty);
    }
    this.items.forEach(item => {
      const row = document.createElement('div');
      row.className = 'ux-favorites-row';
      const link = document.createElement('a');
      link.href = item.url;
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 72;
      image.height = 90;
      const title = document.createElement('span');
      title.textContent = item.title;
      link.append(image, title);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.uxRemove = item.id;
      remove.textContent = '×';
      remove.setAttribute('aria-label', `${this.dataset.remove}: ${item.title}`);
      row.append(link, remove);
      fragment.append(row);
    });
    list.replaceChildren(fragment);
  }
}

if (!customElements.get('ux-favorites')) customElements.define('ux-favorites', UxFavorites);
